import Splide, { Options } from '@splidejs/splide';
import { OutlineElement } from '../../base/outline-element/outline-element';
import { customElement, property, query, state } from 'lit/decorators.js';
import { CSSResultGroup, html } from 'lit';
import componentStyles from './tufts-image-carousel.css.lit';
import { classMap } from 'lit/directives/class-map.js';

/**
 * The Tufts image carousel component
 *
 * @element tufts-image-carousel
 * @slot slides
 *
 * @attr version
 */

@customElement('tufts-image-carousel')
export class TuftsImageCarousel extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  _splide: Splide;

  @query('#splide1')
  _splideRef: HTMLElement;

  @query('.splide__list')
  _splideList: HTMLElement;

  @query('slot')
  _slotRef: HTMLSlotElement;

  /**
   * Version design
   */
  @property({ type: String, reflect: true, attribute: 'version' })
  version = 'default';

  /**
   * Number sliders
   */
  @state() slidesPerPage: number;

  @state() numberSlide: number = 0;

  firstUpdated() {
    this.numberSlide = this._slotRef.assignedNodes().length;

    // Determine the number of slides to display per page based on the total slides
    this.slidesPerPage = this.numberSlide === 1 ? 1 : 2;
    this.updateSlides();
  }

  // This method updates the slides in the carousel.
  // It gets the nodes assigned to the slot, filters out any that are not HTMLElements,
  // converts the remaining nodes to HTML strings, and inserts them into the carousel.
  updateSlides() {
    const slides = this._slotRef
      .assignedNodes()
      .filter(node => node instanceof HTMLElement)
      .map(node => {
        const element = node as HTMLElement;
        element.classList.add('splide__slide');
        if (this.version === 'awards') {
          element.classList.add('awards');
        }
        return element.outerHTML;
      })
      .join('');

    this._splideList.innerHTML = slides;
    this.createReferenceSplide();
  }

  createReferenceSplide() {
    if (this._splideRef) {
      const config: Options =
        this.version === 'default'
          ? {
              type: 'loop',
              perPage: 1,
              focus: 'center',
              interval: 8000,
              updateOnMove: true,
              padding: '15%',
              breakpoints: {
                640: {
                  pagination: false,
                },
              },
            }
          : {
              pagination: false,
              perPage: this.slidesPerPage,
              gap: '1rem',
              padding: {
                left: 50,
                right: 50,
              },
              breakpoints: {
                768: {
                  perPage: 1,
                },
              },
            };

      this._splide = new Splide(this._splideRef, config).mount();
    }
  }

  render() {
    const classes = {
      splide: true,
      noArrows: this.version === 'awards' && this.numberSlide <= 2,
    };
    return html`
      <div id="splide1" class="${classMap(classes)}">
        <div class="splide__track">
          <ul class="splide__list">
            <slot name="slides"></slot>
          </ul>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'tufts-image-carousel': TuftsImageCarousel;
  }
}
